import localforage from 'localforage'

const DB_NAME = 'maidmate-offline'
const STORES = {
  TASKS: 'tasks',
  EXPENSES: 'expenses',
  QUOTATIONS: 'quotations',
  MEALPLANS: 'mealplans',
  OPERATIONS: 'operations',
  SYNC_QUEUE: 'syncQueue'
}

class OfflineDB {
  private stores: Map<string, LocalForage> = new Map()

  constructor() {
    Object.values(STORES).forEach(storeName => {
      this.stores.set(
        storeName,
        localforage.createInstance({ name: DB_NAME, storeName })
      )
    })
  }

  private getStore(storeName: string): LocalForage {
    let store = this.stores.get(storeName)
    if (!store) {
      store = localforage.createInstance({ name: DB_NAME, storeName })
      this.stores.set(storeName, store)
    }
    return store
  }

  async saveItem<T>(storeName: string, item: T & { id: string }): Promise<void> {
    const store = this.getStore(storeName)
    await store.setItem(item.id, item)
  }

  async getItem<T>(storeName: string, id: string): Promise<T | null> {
    const store = this.getStore(storeName)
    return await store.getItem<T>(id)
  }

  async getAllItems<T>(storeName: string): Promise<T[]> {
    const store = this.getStore(storeName)
    const items: T[] = []
    await store.iterate<T, void>((value) => {
      items.push(value)
    })
    return items
  }

  async deleteItem(storeName: string, id: string): Promise<void> {
    const store = this.getStore(storeName)
    await store.removeItem(id)
  }

  async clearStore(storeName: string): Promise<void> {
    const store = this.getStore(storeName)
    await store.clear()
  }

  async addToSyncQueue(operation: {
    id: string
    type: 'create' | 'update' | 'delete'
    resource: string
    resourceId: string
    data: Record<string, unknown>
    timestamp: string
  }): Promise<void> {
    const store = this.getStore(STORES.SYNC_QUEUE)
    await store.setItem(operation.id, operation)
  }

  async getSyncQueue(): Promise<typeof pendingOps> {
    const store = this.getStore(STORES.SYNC_QUEUE)
    const pendingOps: Array<{
      id: string
      type: 'create' | 'update' | 'delete'
      resource: string
      resourceId: string
      data: Record<string, unknown>
      timestamp: string
    }> = []
    await store.iterate<typeof pendingOps[0], void>((value) => {
      pendingOps.push(value)
    })
    return pendingOps.sort((a, b) => 
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    )
  }

  async removeFromSyncQueue(id: string): Promise<void> {
    const store = this.getStore(STORES.SYNC_QUEUE)
    await store.removeItem(id)
  }

  async clearSyncQueue(): Promise<void> {
    await this.clearStore(STORES.SYNC_QUEUE)
  }
}

export const offlineDB = new OfflineDB()
export { STORES }
